import { useId, useState } from 'react';
import { createId, effectiveLocationId, toDateInputValue, type Copy } from '@physical-movies/core';
import { useCollection, useI18n } from '../app/context';
import { FormatChoice } from './FormatChoice';
import { Sheet } from './Sheet';
import { Button, Field, Select, Switch, TextArea, TextInput } from './ui';
import styles from './copySheet.module.css';

const emptyCopy = (): Copy => ({
  id: createId(),
  format: 'hd',
  edition: null,
  notes: null,
  purchasedAt: null,
  loan: null,
});

/**
 * Add or edit one case on the shelf, including who currently has it.
 *
 * Like the watch sheet, the form seeds itself once, so the caller gives it a
 * `key` that changes whenever the sheet is opened on a different copy.
 */
export function CopySheet({
  open,
  onClose,
  initial,
  onSubmit,
  onDelete,
}: {
  open: boolean;
  onClose(): void;
  /** Null when adding. */
  initial: Copy | null;
  onSubmit(copy: Copy): void | Promise<void>;
  onDelete?: () => void | Promise<void>;
}) {
  const { t } = useI18n();
  const { settings } = useCollection();
  const fieldId = useId();
  const locations = settings.locations;

  const [draft, setDraft] = useState<Copy>(() => initial ?? emptyCopy());
  const [lentOut, setLentOut] = useState(Boolean(initial?.loan));
  const [borrower, setBorrower] = useState(initial?.loan?.to ?? '');
  const [since, setSince] = useState(initial?.loan?.since ?? toDateInputValue(new Date()));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const patch = (values: Partial<Copy>) => setDraft((current) => ({ ...current, ...values }));

  const submit = async () => {
    if (lentOut && !borrower.trim()) {
      setError(t('copy.lentNeedsName'));
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        ...draft,
        edition: draft.edition?.trim() || null,
        notes: draft.notes?.trim() || null,
        purchasedAt: draft.purchasedAt || null,
        loan: lentOut ? { to: borrower.trim(), since, note: null } : null,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={initial ? t('copy.title') : t('copy.add')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy}>
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <FormatChoice
          value={draft.format}
          onChange={(format) => patch({ format })}
          label={t('format.label')}
        />

        {locations.length > 0 && (
          <Field label={t('copy.location')} htmlFor={`${fieldId}-where`}>
            <Select
              id={`${fieldId}-where`}
              value={effectiveLocationId(draft, settings) ?? ''}
              onChange={(event) => patch({ locationId: event.target.value })}
            >
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t('copy.edition')} optional={t('common.optional')} htmlFor={`${fieldId}-ed`}>
          <TextInput
            id={`${fieldId}-ed`}
            value={draft.edition ?? ''}
            maxLength={80}
            placeholder={t('copy.editionPlaceholder')}
            onChange={(event) => patch({ edition: event.target.value })}
          />
        </Field>

        <Field
          label={t('copy.purchasedAt')}
          optional={t('common.optional')}
          htmlFor={`${fieldId}-bought`}
        >
          <input
            id={`${fieldId}-bought`}
            type="date"
            className={styles.dateInput}
            value={draft.purchasedAt ?? ''}
            max={toDateInputValue(new Date())}
            onChange={(event) => patch({ purchasedAt: event.target.value || null })}
          />
        </Field>

        <Field label={t('copy.notes')} optional={t('common.optional')} htmlFor={`${fieldId}-notes`}>
          <TextArea
            id={`${fieldId}-notes`}
            value={draft.notes ?? ''}
            maxLength={300}
            placeholder={t('copy.notesPlaceholder')}
            onChange={(event) => patch({ notes: event.target.value })}
          />
        </Field>

        <div className={styles.loanBlock}>
          <Switch checked={lentOut} onChange={setLentOut} label={t('copy.lendOut')} />
          {lentOut && (
            <div className={styles.loanFields}>
              <Field label={t('copy.borrower')} htmlFor={`${fieldId}-who`} error={error}>
                <TextInput
                  id={`${fieldId}-who`}
                  value={borrower}
                  maxLength={60}
                  placeholder={t('copy.borrowerPlaceholder')}
                  onChange={(event) => {
                    setBorrower(event.target.value);
                    setError(null);
                  }}
                />
              </Field>
              <Field label={t('copy.since')} htmlFor={`${fieldId}-since`}>
                <input
                  id={`${fieldId}-since`}
                  type="date"
                  className={styles.dateInput}
                  value={since}
                  max={toDateInputValue(new Date())}
                  onChange={(event) => setSince(event.target.value)}
                />
              </Field>
            </div>
          )}
        </div>

        {onDelete && (
          <Button
            variant="danger"
            block
            onClick={() => {
              if (window.confirm(t('copy.removeConfirm'))) void onDelete();
            }}
          >
            {t('copy.remove')}
          </Button>
        )}
      </div>
    </Sheet>
  );
}
